import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Modal, Form, Input, InputNumber, Select, Tag, Row, Col, Statistic, message, Space, Tabs, Rate, Progress, Avatar } from 'antd';
import { PlusOutlined, TeamOutlined, TrophyOutlined, WarningOutlined } from '@ant-design/icons';
import apiService from 'services/api/apiService';

const readinessColors: Record<string, string> = { ready_now: 'success', ready_1_2_years: 'processing', ready_3_5_years: 'warning', not_ready: 'error' };
const readinessLabels: Record<string, string> = { ready_now: 'Ready Now', ready_1_2_years: '1-2 Years', ready_3_5_years: '3-5 Years', not_ready: 'Not Ready' };
const potentialColors: Record<string, string> = { low: '#d9d9d9', medium: '#91d5ff', high: '#69c0ff', exceptional: '#1890ff' };
const flightColors: Record<string, string> = { low: 'success', medium: 'warning', high: 'error' };

const SuccessionPlanning: React.FC = () => {
  const [talentPool, setTalentPool] = useState<any[]>([]);
  const [plans, setPlans] = useState<any[]>([]);
  const [summary, setSummary] = useState<any>(null);
  const [employees, setEmployees] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [talentModal, setTalentModal] = useState(false);
  const [planModal, setPlanModal] = useState(false);
  const [talentForm] = Form.useForm();
  const [planForm] = Form.useForm();

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [tr, pr, sr, er] = await Promise.all([
        apiService.getTalentPool(),
        apiService.getSuccessionPlans(),
        apiService.getSuccessionSummary(),
        apiService.getEmployees(),
      ]);
      if (tr.success) setTalentPool(tr.data || []);
      if (pr.success) setPlans(pr.data || []);
      if (sr.success) setSummary(sr.data);
      if (er.success) setEmployees(er.data || []);
    } finally { setLoading(false); }
  };

  const handleTalent = async (values: any) => {
    const r = await apiService.addToTalentPool(values);
    if (r.success) { message.success('Added to talent pool'); setTalentModal(false); talentForm.resetFields(); fetchAll(); }
    else message.error(r.error || 'Failed');
  };

  const handlePlan = async (values: any) => {
    const r = await apiService.createSuccessionPlan(values);
    if (r.success) { message.success('Succession plan created'); setPlanModal(false); planForm.resetFields(); fetchAll(); }
    else message.error(r.error || 'Failed');
  };

  const tabItems = [
    {
      key: 'overview',
      label: '📊 Overview',
      children: (
        <Row gutter={16}>
          <Col span={4}><Card><Statistic title="Talent Pool" value={summary?.talent_pool_size || 0} prefix={<TeamOutlined />} /></Card></Col>
          <Col span={4}><Card><Statistic title="High Potential" value={summary?.high_potential || 0} valueStyle={{ color: '#1890ff' }} /></Card></Col>
          <Col span={4}><Card><Statistic title="Ready Now" value={summary?.ready_now || 0} valueStyle={{ color: '#52c41a' }} /></Card></Col>
          <Col span={4}><Card><Statistic title="Flight Risk" value={summary?.flight_risk_high || 0} valueStyle={{ color: '#ff4d4f' }} prefix={<WarningOutlined />} /></Card></Col>
          <Col span={4}><Card><Statistic title="Positions Planned" value={summary?.positions_planned || 0} /></Card></Col>
          <Col span={4}><Card><Statistic title="Critical Positions" value={summary?.critical_positions || 0} valueStyle={{ color: '#ff4d4f' }} /></Card></Col>
        </Row>
      )
    },
    {
      key: 'talent',
      label: '🌟 Talent Pool',
      children: (
        <Card extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => setTalentModal(true)}>Add to Talent Pool</Button>}>
          <Table dataSource={talentPool} rowKey="id" loading={loading}
            columns={[
              { title: 'Employee', dataIndex: 'employee_name', render: (v: string, r: any) => <><Avatar style={{ background: '#1890ff', marginRight: 8 }}>{v?.charAt(0)}</Avatar>{v}<div style={{ fontSize: 12, color: '#666' }}>{r.department} • {r.position}</div></> },
              { title: 'Potential', dataIndex: 'potential_level', render: (v: string) => <Tag color={potentialColors[v]}>{v?.toUpperCase()}</Tag> },
              { title: 'Performance', dataIndex: 'performance_rating', render: (v: number) => v ? <Rate disabled defaultValue={v} count={5} style={{ fontSize: 14 }} /> : '-' },
              { title: 'Readiness', dataIndex: 'readiness', render: (v: string) => <Tag color={readinessColors[v]}>{readinessLabels[v]}</Tag> },
              { title: 'Target Role', dataIndex: 'target_position', render: (v: string) => v || '-' },
              { title: 'Flight Risk', dataIndex: 'flight_risk', render: (v: string) => <Tag color={flightColors[v]}>{v?.toUpperCase()}</Tag> },
              { title: 'Last Reviewed', dataIndex: 'last_reviewed' },
            ]} />
        </Card>
      )
    },
    {
      key: 'plans',
      label: '🗺️ Succession Plans',
      children: (
        <Card extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => setPlanModal(true)}>Create Plan</Button>}>
          <Table dataSource={plans} rowKey="id" loading={loading}
            columns={[
              { title: 'Position', dataIndex: 'position_title' },
              { title: 'Department', dataIndex: 'department' },
              { title: 'Criticality', dataIndex: 'criticality', render: (v: string) => <Tag color={v==='critical'?'red':v==='high'?'orange':v==='medium'?'blue':'default'}>{v?.toUpperCase()}</Tag> },
              { title: 'Incumbent', dataIndex: 'incumbent_name', render: (v: string) => v || '-' },
              { title: '1st Successor', key: 's1', render: (_: any, r: any) => r.successor_1_name ? <><div>{r.successor_1_name}</div><Tag color={readinessColors[r.successor_1_readiness]} style={{ fontSize: 10 }}>{readinessLabels[r.successor_1_readiness]}</Tag></> : '-' },
              { title: '2nd Successor', key: 's2', render: (_: any, r: any) => r.successor_2_name ? <><div>{r.successor_2_name}</div><Tag color={readinessColors[r.successor_2_readiness]} style={{ fontSize: 10 }}>{readinessLabels[r.successor_2_readiness]}</Tag></> : '-' },
              { title: 'Review Date', dataIndex: 'review_date' },
            ]} />
        </Card>
      )
    },
  ];

  const empOptions = employees.map(e => <Select.Option key={e.id} value={e.id}>{e.first_name} {e.last_name} — {e.department}</Select.Option>);

  return (
    <div style={{ padding: 24 }}>
      <h1>Succession Planning</h1>
      <Tabs items={tabItems} />

      {/* Talent Pool Modal */}
      <Modal title="Add to Talent Pool" open={talentModal} onCancel={() => { setTalentModal(false); talentForm.resetFields(); }} footer={null} width={600}>
        <Form form={talentForm} layout="vertical" onFinish={handleTalent}>
          <Form.Item name="employee_id" label="Employee" rules={[{ required: true }]}><Select showSearch optionFilterProp="children">{empOptions}</Select></Form.Item>
          <Row gutter={16}>
            <Col span={12}><Form.Item name="potential_level" label="Potential Level" initialValue="medium"><Select><Select.Option value="low">Low</Select.Option><Select.Option value="medium">Medium</Select.Option><Select.Option value="high">High</Select.Option><Select.Option value="exceptional">Exceptional</Select.Option></Select></Form.Item></Col>
            <Col span={12}><Form.Item name="performance_rating" label="Performance Rating (1-5)"><InputNumber min={1} max={5} step={0.1} style={{ width: '100%' }} /></Form.Item></Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}><Form.Item name="readiness" label="Readiness" initialValue="ready_1_2_years"><Select>{Object.entries(readinessLabels).map(([k,v]) => <Select.Option key={k} value={k}>{v}</Select.Option>)}</Select></Form.Item></Col>
            <Col span={12}><Form.Item name="flight_risk" label="Flight Risk" initialValue="low"><Select><Select.Option value="low">Low</Select.Option><Select.Option value="medium">Medium</Select.Option><Select.Option value="high">High</Select.Option></Select></Form.Item></Col>
          </Row>
          <Form.Item name="target_position" label="Target Position"><Input placeholder="e.g. Head of Department" /></Form.Item>
          <Form.Item name="strengths" label="Key Strengths"><Input.TextArea rows={2} /></Form.Item>
          <Form.Item name="development_areas" label="Development Areas"><Input.TextArea rows={2} /></Form.Item>
          <Form.Item name="last_reviewed" label="Review Date"><Input type="date" defaultValue={new Date().toISOString().split('T')[0]} /></Form.Item>
          <Space><Button type="primary" htmlType="submit">Add to Pool</Button><Button onClick={() => setTalentModal(false)}>Cancel</Button></Space>
        </Form>
      </Modal>

      {/* Succession Plan Modal */}
      <Modal title="Create Succession Plan" open={planModal} onCancel={() => { setPlanModal(false); planForm.resetFields(); }} footer={null} width={650}>
        <Form form={planForm} layout="vertical" onFinish={handlePlan}>
          <Row gutter={16}>
            <Col span={16}><Form.Item name="position_title" label="Position Title" rules={[{ required: true }]}><Input /></Form.Item></Col>
            <Col span={8}><Form.Item name="criticality" label="Criticality" initialValue="medium"><Select><Select.Option value="low">Low</Select.Option><Select.Option value="medium">Medium</Select.Option><Select.Option value="high">High</Select.Option><Select.Option value="critical">Critical</Select.Option></Select></Form.Item></Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}><Form.Item name="department" label="Department"><Input /></Form.Item></Col>
            <Col span={12}><Form.Item name="incumbent_id" label="Current Incumbent"><Select allowClear showSearch optionFilterProp="children">{empOptions}</Select></Form.Item></Col>
          </Row>
          {[1,2,3].map(n => (
            <Row gutter={16} key={n}>
              <Col span={14}><Form.Item name={`successor_${n}_id`} label={`${n}${n===1?'st':n===2?'nd':'rd'} Successor`}><Select allowClear showSearch optionFilterProp="children">{empOptions}</Select></Form.Item></Col>
              <Col span={10}><Form.Item name={`successor_${n}_readiness`} label="Readiness" initialValue="ready_1_2_years"><Select>{Object.entries(readinessLabels).filter(([k]) => k !== 'not_ready').map(([k,v]) => <Select.Option key={k} value={k}>{v}</Select.Option>)}</Select></Form.Item></Col>
            </Row>
          ))}
          <Row gutter={16}>
            <Col span={12}><Form.Item name="review_date" label="Review Date"><Input type="date" /></Form.Item></Col>
          </Row>
          <Form.Item name="notes" label="Notes"><Input.TextArea rows={2} /></Form.Item>
          <Space><Button type="primary" htmlType="submit">Create Plan</Button><Button onClick={() => setPlanModal(false)}>Cancel</Button></Space>
        </Form>
      </Modal>
    </div>
  );
};

export default SuccessionPlanning;
